import type { AgentAbout } from '@ralph/contracts'
import { ArrowLeft, ArrowRight, Eye, Lock } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { accentTileStyle } from '@/components/marketplace/accents.ts'
import { OfficialMark } from '@/components/marketplace/consumer/AgentBadges.tsx'
import { AudienceLabel } from '@/components/marketplace/consumer/AudienceLabel.tsx'
import { monogram } from '@/components/marketplace/consumer/agent-identity.ts'
import { DisclosurePanel } from '@/components/marketplace/consumer/DisclosurePanel.tsx'
import { OwnerLabel } from '@/components/marketplace/consumer/OwnerLabel.tsx'
import { BuilderSection, Note } from '@/components/marketplace/maker/BuilderShell.tsx'
import { Badge, Readout } from '@/components/ui/primitives.tsx'
import { useI18n } from '@/i18n/index.ts'

/** The way back, in the same mono pill every secondary agent surface uses to go up a level. */
const BACK_PILL =
  'readout inline-flex h-[29px] items-center gap-[7px] rounded-full border border-rule bg-card pr-3 pl-[9px] text-[10px] tracking-[0.05em] text-fg-muted transition-[color,border-color,transform] duration-200 hover:-translate-y-px hover:border-rule-strong hover:text-fg'

/**
 * The detail sheet at consumer fidelity (PRD-22, DTL-011).
 *
 * **It is assembled from `AgentAbout` and from nothing else, and that is the design.** The
 * obvious alternative — relax the maker read to anybody who can see the listing and redact
 * the private fields — makes every future field on `MakerAgent` leak by default, because a
 * redaction is a deletion and deletions have to be remembered. This component cannot leak
 * one: its only input is the payload the listing page already sends this reader, authorized
 * by `canReadAbout`, computed per viewer, and it has never carried an owner-only field. A new
 * private field on the maker plane is invisible here by construction rather than by
 * discipline. (PRD-22 D-1.)
 *
 * The consequence, stated on the page rather than hidden by it: the instructions are not
 * here. What the agent *does* with them is, in the disclosure panel below — which the server
 * computes from the resolved definition and no maker can type into (AGM-003). Withholding the
 * prose and publishing the behaviour is the line PRD-18 drew, and this page keeps it.
 *
 * It borrows the builder's furniture on purpose: the same masthead, the same numbered
 * `BuilderSection` panels, the same order. Somebody who has seen the editable sheet should
 * recognise this as the same page at a different fidelity, which is the whole reason the two
 * are one route.
 *
 * Every write is **absent**, never disabled (DTL-023). A greyed-out Save on a page nobody may
 * save is furniture that has to be explained; a page with no Save explains itself.
 */
export const AgentDetailsSheet = ({ about }: { about: AgentAbout }) => {
  // Bound inside the body, never at module scope: a captured locale freezes the language a
  // date renders in — and freezes the whole `details` pack with it — with no type error and
  // no failing test to say so (PRD-27 F-3, LOC-012).
  const { t: messages, formatDate } = useI18n()
  const details = messages.marketplace.gallery.details
  // The three masthead labels the listing page uses too, taken from the same entries so the
  // two surfaces cannot disagree about one audience. Named for what they are, because `about`
  // is already this component's payload.
  const labels = messages.marketplace.gallery.about

  return (
    <div className="fade-in min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-[1080px] px-4 pt-1 pb-9 sm:px-6 lg:px-8">
        <Link to="/agents" className={`${BACK_PILL} mt-3.5 mb-5`}>
          <ArrowLeft size={12} strokeWidth={2.2} aria-hidden />
          {details.allAgents}
        </Link>

        <header className="flex items-start gap-3.5 sm:gap-4">
          {/* The agent's own tile at the builder's `lg` size, built here rather than through
              `AgentAvatar` only because the masthead pairs it with a serif display name and the
              two have to share a baseline. The fill and the depth are still `accentTileStyle`. */}
          <span
            aria-hidden
            style={accentTileStyle(about.accent)}
            className="flex h-[56px] w-[56px] flex-none select-none items-center justify-center rounded-[19px] font-mono text-[25px] font-semibold tracking-[0.02em]"
          >
            {about.icon || monogram(about.displayName)}
          </span>

          <div className="min-w-0 flex-1 pt-0.5">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <h1 className="display min-w-0 break-words text-[26px] sm:text-[34px]">{about.displayName}</h1>
              {about.official && <OfficialMark />}
            </div>
            {about.tagline && (
              <p className="mt-2 max-w-[524px] text-pretty text-[13.5px] leading-[1.55] text-fg-muted">
                {about.tagline}
              </p>
            )}

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Link to={`/agents/${encodeURIComponent(about.agentId)}`} className={BACK_PILL}>
                {details.backToAgent}
                <ArrowRight size={12} strokeWidth={2.2} aria-hidden />
              </Link>
            </div>
          </div>
        </header>

        {/*
          Why this page is read-only, said once and near the top. `Eye` rather than `Lock`: the
          reader has not been refused anything, they are looking at somebody else's agent — and
          a padlock on a page that opened fine reads as a wall rather than as an explanation.
          The padlock stays where it belongs, on the withheld instructions below.
        */}
        <div className="mt-4">
          <Note tone="info" icon={<Eye size={13} strokeWidth={2} />}>
            {details.readOnly}
          </Note>
        </div>

        <div className="mt-4 space-y-3.5">
          <BuilderSection id="details-identity" title={details.identityTitle} lead={details.identityLead}>
            <dl className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
              <Fact label={details.nameLabel} value={about.displayName} />
              <Fact label={details.categoryLabel} value={about.category} />
              <Fact label={details.taglineLabel} value={about.tagline} className="sm:col-span-2" />
            </dl>

            <div>
              <p className="eyebrow mb-1.5 text-fg-muted">{details.descriptionLabel}</p>
              {about.description ? (
                <p className="whitespace-pre-wrap text-pretty text-[13.5px] leading-[1.6]">{about.description}</p>
              ) : (
                <p className="text-[13px] text-fg-subtle">{details.descriptionEmpty}</p>
              )}
            </div>
          </BuilderSection>

          {/*
            The hole, with its edges drawn. The section is numbered and titled exactly as it is
            on the editable sheet, so a reader can see that a thing exists here and that they
            are not being shown it — which is a different statement from the section quietly not
            rendering, and the only honest one.
          */}
          <BuilderSection id="details-instructions" title={details.instructionsTitle} lead={details.instructionsLead}>
            <Note tone="info" icon={<Lock size={13} strokeWidth={2} />}>
              <span className="font-semibold">{details.instructionsWithheld}</span> {details.instructionsWithheldBody}
            </Note>
          </BuilderSection>

          {/*
            The disclosure panel carries steps 3 to 5 of the editable sheet — knowledge, what it
            can do, and how it behaves — in one authoritative block rather than three restated
            ones. Restating it here would be a second implementation of AGM-003's "pure function
            of the resolved definition", and the second implementation is the one that drifts.
          */}
          <DisclosurePanel disclosure={about.disclosure} agentId={about.agentId} agentName={about.displayName} />

          <BuilderSection id="details-starters" title={details.startersTitle} lead={details.startersLead}>
            {about.starters.length > 0 ? (
              <ul className="space-y-1.5">
                {about.starters.map((starter) => (
                  <li
                    key={starter}
                    className="flex items-start gap-2 rounded-tile border border-rule bg-card-solid px-3 py-2 text-pretty text-[13px] leading-snug text-fg-muted"
                  >
                    <ArrowRight size={12} strokeWidth={2.2} className="mt-[3px] flex-none text-fg-subtle" aria-hidden />
                    <span className="min-w-0 flex-1">{starter}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[13px] text-fg-subtle">{details.startersEmpty}</p>
            )}
          </BuilderSection>

          <BuilderSection id="details-audience" title={details.audienceTitle} lead={details.audienceLead}>
            <dl className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
              <Fact
                label={labels.audienceLabel}
                value={<AudienceLabel audience={about.audience} owner={about.owner} />}
                className="sm:col-span-2"
              />
              <Fact label={labels.ownerLabel} value={<OwnerLabel owner={about.owner} to />} />
            </dl>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2.5">
              <Readout label={labels.updatedLabel} value={formatDate(about.updatedAt)} />
              <span className="inline-flex items-center gap-1.5">
                <span className="readout text-fg-subtle">{details.stateLabel}</span>
                <Badge
                  tone={about.state === 'published' ? 'grounded' : about.state === 'suspended' ? 'alert' : 'neutral'}
                >
                  {details.state[about.state]}
                </Badge>
              </span>
            </div>
          </BuilderSection>
        </div>
      </div>
    </div>
  )
}

/**
 * One read-only field, in the shape the editable sheet gives the same value.
 *
 * A `<dt>`/`<dd>` pair rather than a disabled `<input>`: a disabled input is still announced
 * as a form control, so a screen reader walking this page would be told about eleven fields
 * it cannot fill in. A description list says what this actually is — facts with names.
 */
const Fact = ({ label, value, className }: { label: string; value: ReactNode; className?: string }) => (
  <div className={className}>
    <dt className="eyebrow mb-1 text-fg-muted">{label}</dt>
    <dd className="text-pretty text-[13.5px] leading-[1.5]">{value || <span className="text-fg-subtle">—</span>}</dd>
  </div>
)
